import { fetchGet } from "app/utils/request";
import { FamilyMember } from "app/routes/Account/FamilyEdit/types";

/**
 * Shared, in-flight-deduped cache for the caller's family roster
 * (`/api/v2/family_memberships`), CN-1323 (CFTB-3301).
 *
 * The roster is fetched once per page view and shared by every
 * `useBookingParticipants` consumer. Without this, a grid of N offering cards
 * fired N identical `family_memberships` GETs (one per `AthleteOffering`). A
 * single module-level promise means concurrent callers join the same request,
 * and later mounts reuse the resolved value.
 *
 * The roster doesn't change during a page view; `clearFamilyRosterCache`
 * invalidates it after a member is added/edited so the next read refetches.
 */
let rosterPromise: Promise<FamilyMember[]> | null = null;

export function getFamilyRoster(): Promise<FamilyMember[]> {
  if (!rosterPromise) {
    rosterPromise = fetchGet<FamilyMember>("family_memberships", {}, { version: 2 })
      .then((roster) => {
        const list = Array.isArray(roster) ? roster : [];
        return list.filter((m) => m.role != null);
      })
      .catch((err) => {
        // Don't cache a rejected promise — a transient failure (e.g. not yet
        // entitled) shouldn't permanently pin callers to "self only".
        rosterPromise = null;
        throw err;
      });
  }
  return rosterPromise;
}

export function clearFamilyRosterCache(): void {
  rosterPromise = null;
}
